import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import type { DropdownOption } from '~/components/Dropdown/types/DropdownOption';
import { Icon } from '~/components/Icon';
import { IconButton } from '~/components/IconButton';
import { chevron } from '~/components/styles';
import { cn } from '~/lib/cn';
import type { MediaPlatform } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/constants';
import { PLATFORM_MAP } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/constants';
import type {
  MediaPlanForm,
  PlanWizardForm,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';

import { DeleteRowModal } from './DeleteRowModal';
import { MediaPlanRow } from './MediaPlanRow';

interface PlatformProps {
  platformIndex: number;
  platform: MediaPlatform;
  onRemove: () => void;
  marketOptions: DropdownOption[];
  goalOptions: DropdownOption[];
  plan: PlanWizardForm;
}

export function Platform({
  platformIndex,
  platform,
  onRemove,
  marketOptions,
  goalOptions,
  plan,
}: PlatformProps) {
  const { t } = useTranslation('campaign-planner');
  const { t: nameTranslation } = useTranslation('names');

  const { control, getValues } = useFormContext<MediaPlanForm>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | undefined>(undefined);

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: `platforms.${platformIndex}.plans`,
  });

  const handleAddRowButtonClick = () => {
    append({
      market: plan.markets[0],
      budget: plan.totalBudget,
      audienceSize: 0,
      frequency: {
        times: plan.frequency?.times ?? 1,
        period: plan.frequency?.period ?? 'week',
      },
    });
  };

  const handleDuplicateRowButtonClick = (rowIndex: number) => {
    const currentPlans = getValues(`platforms.${platformIndex}.plans`);
    const planToDuplicate = currentPlans[rowIndex];

    if (planToDuplicate) {
      insert(rowIndex + 1, { ...planToDuplicate });
    }
  };

  const handleDeleteRowButtonClick = (rowIndex: number) => {
    setRowToDelete(rowIndex);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete !== undefined) {
      remove(rowToDelete);
      setRowToDelete(undefined);
    }
  };

  const platformConfig = PLATFORM_MAP[platform];
  const dataColorVar = `--data-color-0${platformConfig?.dataColor}`;

  return (
    <div className="mb-sm border-b border-emphasis-subtle">
      {/* Platform Header */}
      <div className="mb-xs flex items-center justify-between">
        <div className="flex items-center gap-px text-caption-md font-bold">
          <div
            className="size-sm rounded-full"
            style={{ backgroundColor: `oklch(var(${dataColorVar}))` }}
          />

          <div className="flex w-lg justify-center text-center">
            <Icon
              type={platformConfig.icon}
              variant="brands"
              size="lg"
              className="text-default"
            />
          </div>

          <span>{nameTranslation(platformConfig.labelKey)}</span>
        </div>
        <div className="flex items-center gap-xs">
          {fields.length === 0 ? (
            <IconButton
              iconType="trash"
              size="small"
              onClick={onRemove}
              aria-label={t(
                'digitalCampaigns.mediaPlan.platforms.removePlatform',
              )}
            />
          ) : null}
          <button
            aria-label={
              isCollapsed
                ? t('digitalCampaigns.mediaPlan.platforms.expandLabel')
                : t('digitalCampaigns.mediaPlan.platforms.collapseLabel')
            }
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex size-xl items-center rounded-full transition-all duration-300 ease-out hover:bg-emphasis-strong active:bg-primary-default"
          >
            {/* @TODO WMG1-825 - Use ChevronIcon component */}
            <Icon
              className={cn(
                chevron({ state: isCollapsed ? 'default' : 'open' }),
                'static',
              )}
              size="sm"
              type="chevron-down"
            />
          </button>
        </div>
      </div>

      {/* Platform Rows */}
      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
        )}
      >
        <div className="overflow-hidden">
          {/* Table Rows */}
          {fields.map((field, rowIndex) => (
            <MediaPlanRow
              key={field.id}
              platformIndex={platformIndex}
              rowIndex={rowIndex}
              fieldId={field.id}
              fieldGoal={field.goal}
              marketOptions={marketOptions}
              goalOptions={goalOptions}
              onDuplicate={() => handleDuplicateRowButtonClick(rowIndex)}
              onDelete={() => handleDeleteRowButtonClick(rowIndex)}
            />
          ))}

          {/* Add Row Button */}
          <div className="mb-md mr-px mt-sm flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={handleAddRowButtonClick}
              className="w-fit"
              aria-label={t('digitalCampaigns.mediaPlan.platforms.addRow')}
            >
              {t('digitalCampaigns.mediaPlan.platforms.addRow')}
              <Icon
                type="plus"
                variant="solid"
                size="sm"
                className="-mr-2xs ml-sm"
              />
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteRowModal
        isOpen={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        platformName={nameTranslation(platformConfig.labelKey)}
      />
    </div>
  );
}

Platform.displayName = 'Platform';
